import {
  ExternalExportStatus,
  OrderUnitOfMeasure,
  Principal_Read_Nested,
  Ticket_Metadata_Read,
  Ticket_Read,
  Ticket_Read_Nested,
  Ticket_Update,
  TicketRollupSummary_Read,
  TicketSource,
  TicketState,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';

import { BasicMaterial } from '~hooks/useMaterials/models';
import { ItemNameAndId } from '~types/ItemNameAndId';
import { Nullable } from '~types/Nullable';
import { parseTicketQuantity, toNullableId } from '~utils/tickets/ticket-input';

export class TicketBasic {
  constructor(
    private _id: Nullable<string>,
    private _ticketNumber: Nullable<string>,
    private _previewUrl: Nullable<string>,
    private _discarded_at: Nullable<Dayjs> = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get ticketNumber() {
    return this._ticketNumber;
  }

  public get previewUrl() {
    return this._previewUrl;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public static parse(proto: Ticket_Read_Nested) {
    return new TicketBasic(
      proto.id,
      proto.ticket_number,
      proto.preview_url,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
}

export interface TicketUpdateInput {
  flagged?: boolean;
  imageUrl?: string;
  machineId?: string;
  material?: { id?: string | null } | null;
  quantity?: string | null;
  serviceDate?: Date | string | null;
  ticketNumber?: string;
  trailerId?: string;
  unitOfMeasure?: { id?: string | null } | null;
}

export class Ticket {
  public static parse(proto: Ticket_Read): Ticket {
    return new Ticket(
      proto.id ?? '',
      proto.created_at ?? '',
      proto.updated_at ?? '',
      proto.quantity?.length ? proto.quantity : null,
      proto.ticket_number ?? '',
      proto.unit_of_measure
        ? ItemNameAndId.parse({
            name: proto.unit_of_measure,
            id: proto.unit_of_measure,
          })
        : null,
      proto.job_id ?? '',
      proto.load_id ?? '',
      proto.material?.id ? BasicMaterial.parse(proto.material) : null,
      proto.image_url ?? '',
      proto?.ocr_processed ?? false,
      proto.ocr_ticket_number ?? '',
      proto.ocr_net_quantity_ton ?? '',
      proto.ocr_service_date ?? '',
      proto.service_date ?? '',
      proto.source ?? null,
      proto.customer_name ?? '',
      proto.project_name ?? '',
      proto.equipment_name ?? '',
      proto.driver_name ?? '',
      proto.vendor_name ?? '',
      proto.material_name ?? '',
      proto.pickup_site_name ?? '',
      proto.dropoff_site_name ?? '',
      proto.friendly_job_id ?? '',
      proto.dispatch_number_name ?? '',
      proto.state ?? '',
      proto.flagged ?? false,
      proto.missing_info ?? false,
      proto.preview_url ?? '',
      proto.ai_processed ?? false,
      proto.external_truck_id,
      proto.external_vendor_name,
      proto.external_material_material_name,
      proto.external_net_weight,
      proto.external_net_weight_unit,
      proto.external_ticket_number,
      proto.image_mime_type,
      proto.potential_duplicate ?? false,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
      proto.potential_duplicate_dismissed_at
        ? dayjs(proto.potential_duplicate_dismissed_at)
        : null,
      proto.potential_duplicate_dismissed_by ?? null,
      proto.external_gross_weight,
      proto.external_tare_weight,
      proto.external_load_number,
      proto.external_scale_id,
      proto.external_silo_id,
      proto.external_mix_design_id,
      proto.external_cumulative_daily_quantity,
      proto.external_trailer_id,
      proto.external_machine_id,
      proto.external_weighmaster_name,
      proto.external_item_id,
      proto.external_overweight_weight,
      proto.external_cumulative_daily_overweight_quantity,
      proto.external_export_status,
      proto.external_export_reason,
      proto.veta_center_bound,
    );
  }
  public static deparseUpdate(
    data: TicketUpdateInput,
  ): Ticket_Update & { image_url?: string } {
    return {
      quantity: parseTicketQuantity(data.quantity) ?? undefined,
      unit_of_measure: (data.unitOfMeasure?.id || undefined) as
        | OrderUnitOfMeasure
        | undefined,
      material_id: data.material?.id ?? undefined,
      ticket_number: data.ticketNumber,
      flagged: data.flagged,
      image_url: data.imageUrl,
      service_date:
        data.serviceDate instanceof Date
          ? data.serviceDate.toISOString()
          : (data.serviceDate ?? undefined),
      external_trailer_id: toNullableId(data.trailerId),
      external_machine_id: toNullableId(data.machineId),
    };
  }
  public get id(): string {
    return this._id;
  }
  public get createdAt(): string {
    return this._createdAt;
  }
  public get updatedAt(): string {
    return this._updatedAt;
  }
  public get quantity(): Nullable<string> {
    return this._quantity;
  }
  public set quantity(value: Nullable<string>) {
    this._quantity = value;
  }
  public get ticketNumber(): string {
    return this._ticketNumber;
  }
  public set ticketNumber(value: string) {
    this._ticketNumber = value;
  }
  public get unitOfMeasure(): Nullable<ItemNameAndId> {
    return this._unitOfMeasure;
  }
  public set unitOfMeasure(value: Nullable<ItemNameAndId>) {
    this._unitOfMeasure = value;
  }
  public get jobId(): string {
    return this._jobId;
  }
  public get loadId(): string {
    return this._loadId;
  }
  public get material(): Nullable<BasicMaterial> {
    return this._material;
  }
  public set material(value: Nullable<BasicMaterial>) {
    this._material = value;
  }
  public get imageUrl(): string {
    return this._imageUrl;
  }
  public set imageUrl(value: string) {
    this._imageUrl = value;
  }
  public get ocrProcessed(): boolean {
    return this._ocrProcessed;
  }
  public set ocrProcessed(value: boolean) {
    this._ocrProcessed = value;
  }
  public get ocrTicketNumber(): string {
    return this._ocrTicketNumber;
  }
  public get ocrNetQuantityTons(): string {
    return this._ocrNetQuantityTons;
  }
  public get ocrServiceDate(): string {
    return this._ocrServiceDate;
  }
  public get serviceDate(): string {
    return this._serviceDate;
  }

  public get source() {
    return this._source;
  }

  public get customerName(): Nullable<string> {
    return this._customer_name;
  }

  public get projectName(): Nullable<string> {
    return this._project_name;
  }

  public get equipmentName(): Nullable<string> {
    return this._equipment_name;
  }

  public get driverName(): Nullable<string> {
    return this._driver_name;
  }

  public get vendorName(): Nullable<string> {
    return this._vendor_name;
  }

  public get materialName(): Nullable<string> {
    return this._material_name;
  }

  public get pickupSiteName(): Nullable<string> {
    return this._pickup_site_name;
  }

  public get dropoffSiteName(): Nullable<string> {
    return this._dropoff_site_name;
  }

  public get friendlyJobId(): Nullable<string> {
    return this._friendly_job_id;
  }

  public get dispatchNumberName(): Nullable<string> {
    return this._dispatch_number_name;
  }

  public get state(): Nullable<TicketState> {
    return this._state;
  }

  public get flagged(): Nullable<boolean> {
    return this._flagged;
  }

  public set flagged(value: boolean) {
    this._flagged = value;
  }

  public get missingInfo(): boolean {
    return this._missing_info;
  }

  public get previewUrl() {
    return this._preview_url || this._imageUrl;
  }

  public get aiProcessed(): boolean {
    return this._ai_processed;
  }

  public get externalTruckId() {
    return this._external_truck_id;
  }

  public get externalVendorName() {
    return this._external_vendor_name;
  }

  public get externalMaterialMaterialName() {
    return this._external_material_material_name;
  }

  public get externalNetWeight() {
    return this._external_net_weight;
  }

  public get externalNetWeightUnit() {
    return this._external_net_weight_unit;
  }

  public get externalTicketNumber() {
    return this._external_ticket_number;
  }

  public get imageMimeType() {
    return this._image_mime_type;
  }

  public get potentialDuplicate() {
    return this._potential_duplicate;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public get isPotentialDuplicateFlagged() {
    return this._potential_duplicate && !this._potential_duplicate_dismissed_at;
  }

  public get potentialDuplicateDismissedAt() {
    return this._potential_duplicate_dismissed_at;
  }

  public get potentialDuplicateDismissedBy() {
    return this._potential_duplicate_dismissed_by;
  }

  public get externalGrossWeight() {
    return this._external_gross_weight;
  }

  public get externalTareWeight() {
    return this._external_tare_weight;
  }

  public get externalLoadNumber() {
    return this._external_load_number;
  }

  public get externalScaleId() {
    return this._external_scale_id;
  }

  public get externalSiloId() {
    return this._external_silo_id;
  }

  public get externalMixDesignId() {
    return this._external_mix_design_id;
  }

  public get externalCumulativeDailyQuantity() {
    return this._external_cumulative_daily_quantity;
  }

  public get externalTrailerId() {
    return this._external_trailer_id;
  }

  public get externalMachineId() {
    return this._external_machine_id;
  }

  public get externalWeighmasterName() {
    return this._external_weighmaster_name;
  }

  public get externalItemId() {
    return this._external_item_id;
  }

  public get externalOverweightWeight() {
    return this._external_overweight_weight;
  }

  public get externalCumulativeDailyOverweightQuantity() {
    return this._external_cumulative_daily_overweight_quantity;
  }

  public get externalExportStatus() {
    return this._external_export_status;
  }

  public get externalExportReason() {
    return this._external_export_reason;
  }

  public get vetaCenterBound() {
    return this._veta_center_bound;
  }

  constructor(
    private _id: string,
    private _createdAt: string,
    private _updatedAt: string,
    private _quantity: Nullable<string>,
    private _ticketNumber: string,
    private _unitOfMeasure: Nullable<ItemNameAndId>,
    private _jobId: string,
    private _loadId: string,
    private _material: Nullable<BasicMaterial>,
    private _imageUrl: string,
    private _ocrProcessed: boolean,
    private _ocrTicketNumber: string,
    private _ocrNetQuantityTons: string,
    private _ocrServiceDate: string,
    private _serviceDate: string,
    private _source: Nullable<TicketSource>,
    private _customer_name: Nullable<string>,
    private _project_name: Nullable<string>,
    private _equipment_name: Nullable<string>,
    private _driver_name: Nullable<string>,
    private _vendor_name: Nullable<string>,
    private _material_name: Nullable<string>,
    private _pickup_site_name: Nullable<string>,
    private _dropoff_site_name: Nullable<string>,
    private _friendly_job_id: Nullable<string>,
    private _dispatch_number_name: Nullable<string>,
    private _state: Nullable<TicketState>,
    private _flagged: boolean = false,
    private _missing_info: boolean = false,
    private _preview_url: string | null,
    private _ai_processed: boolean,
    private _external_truck_id: Nullable<string>,
    private _external_vendor_name: Nullable<string>,
    private _external_material_material_name: Nullable<string>,
    private _external_net_weight: Nullable<string>,
    private _external_net_weight_unit: Nullable<string>,
    private _external_ticket_number: Nullable<string>,
    private _image_mime_type: Nullable<string>,
    private _potential_duplicate: boolean,
    private _discarded_at: Nullable<Dayjs> = null,
    private _potential_duplicate_dismissed_at: Nullable<Dayjs> = null,
    private _potential_duplicate_dismissed_by: Nullable<Principal_Read_Nested> = null,
    private _external_gross_weight: Nullable<string> = null,
    private _external_tare_weight: Nullable<string> = null,
    private _external_load_number: Nullable<number> = null,
    private _external_scale_id: Nullable<string> = null,
    private _external_silo_id: Nullable<string> = null,
    private _external_mix_design_id: Nullable<string> = null,
    private _external_cumulative_daily_quantity: Nullable<string> = null,
    private _external_trailer_id: Nullable<string> = null,
    private _external_machine_id: Nullable<string> = null,
    private _external_weighmaster_name: Nullable<string> = null,
    private _external_item_id: Nullable<string> = null,
    private _external_overweight_weight: Nullable<string> = null,
    private _external_cumulative_daily_overweight_quantity: Nullable<string> = null,
    private _external_export_status: Nullable<ExternalExportStatus> = null,
    private _external_export_reason: Nullable<string> = null,
    private _veta_center_bound: boolean = false,
  ) {}
}

export class TicketMetadata {
  public static parse(proto: Ticket_Metadata_Read) {
    return new TicketMetadata(
      proto.id ?? '',
      proto.ai_confidence_score ?? '',
      proto.ai_extracted_full_text ?? '',
      proto.ai_structured_data ?? null,
    );
  }

  public get id(): string {
    return this._id;
  }

  public get aiConfidenceScore(): Nullable<string> {
    return this._ai_confidence_score;
  }

  public get aiExtractedFullText(): Nullable<string> {
    return this._ai_extracted_full_text;
  }

  public get aiStructuredData(): Nullable<Record<string, any>> {
    return this._ai_structured_data;
  }

  constructor(
    private _id: string,
    private _ai_confidence_score: Nullable<string>,
    private _ai_extracted_full_text: Nullable<string>,
    private _ai_structured_data: Nullable<Record<string, any>>,
  ) {}
}

export class TicketsRollupSummary {
  public static parse(proto: TicketRollupSummary_Read) {
    return new TicketsRollupSummary(
      proto.external_truck_id,
      proto.external_vendor_name,
      Number(proto.total_tickets_quantity),
      Number(proto.total_tickets_count),
      Number(proto.total_tickets_missing_job_count),
      Number(proto.total_tickets_missing_job_quantity),
      proto.unit_of_measure,
    );
  }

  public get externalTruckId() {
    return this._external_truck_id;
  }

  public get externalVendorName() {
    return this._external_vendor_name;
  }

  public get totalTicketsQuantity() {
    return this._total_tickets_quantity;
  }

  public get totalTicketsCount() {
    return this._total_tickets_count;
  }

  public get totalTicketsMissingJobCount() {
    return this._total_tickets_missing_job_count;
  }

  public get totalTicketsWithJobCount() {
    return this.totalTicketsCount - this.totalTicketsMissingJobCount;
  }

  public get totalTicketsMissingJobQuantity() {
    return this._total_tickets_missing_job_quantity;
  }

  public get totalTicketsWithJobQuantity() {
    return this.totalTicketsQuantity - this.totalTicketsMissingJobQuantity;
  }

  public get unitOfMeasure() {
    return this._unit_of_measure;
  }

  constructor(
    private _external_truck_id: Nullable<string>,
    private _external_vendor_name: Nullable<string>,
    private _total_tickets_quantity: number,
    private _total_tickets_count: number,
    private _total_tickets_missing_job_count: number,
    private _total_tickets_missing_job_quantity: number,
    private _unit_of_measure: Nullable<string>,
  ) {}
}
